import React, { useState, useEffect } from 'react';
import { getAllNews, createNews, updateNews, deleteNews, deactivateNews, toggleNewsActive } from '../api/news';
import { getAllCommitments, createCommitment, updateCommitment, deleteCommitment, deactivateCommitment, toggleCommitmentActive } from '../api/commitments';
import { getAllMainImages, createMainImage, updateMainImage, deleteMainImage, toggleMainImageActive } from '../api/commitmentMainImages';
import '../css/newsAndCommitments.css';

function NewsAndCommitments() {
  const [activeTab, setActiveTab] = useState('news');
  const [newsList, setNewsList] = useState([]);
  const [commitmentsList, setCommitmentsList] = useState([]);
  const [mainImagesList, setMainImagesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // News form data
  const [newsFormData, setNewsFormData] = useState({
    id: '',
    imageUrl: '',
    title: '',
    dateTime: '',
    text: '',
    url: '',
    isActive: true
  });

  // Commitment form data
  const [commitmentFormData, setCommitmentFormData] = useState({
    id: '',
    imageUrl: '',
    title: '',
    text: '',
    url: '',
    isActive: true
  });

  // Main Image form data
  const [mainImageFormData, setMainImageFormData] = useState({
    id: '',
    imageUrl: '',
    isActive: true
  });

  // Load data when component mounts or tab changes
  useEffect(() => {
    if (activeTab === 'news') {
      loadNews();
    } else if (activeTab === 'commitments') {
      loadCommitments();
    } else if (activeTab === 'commitment-main-image') {
      loadMainImages();
    }
  }, [activeTab]);

  const loadNews = async () => {
    setLoading(true);
    try {
      const data = await getAllNews();
      setNewsList(data);
      setError('');
    } catch (err) {
      setError('Failed to load news: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCommitments = async () => {
    setLoading(true);
    try {
      const data = await getAllCommitments();
      setCommitmentsList(data);
      setError('');
    } catch (err) {
      setError('Failed to load commitments: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMainImages = async () => {
    setLoading(true);
    try {
      const data = await getAllMainImages();
      setMainImagesList(data);
      setError('');
    } catch (err) {
      setError('Failed to load main images: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        await updateNews(editingItem._id, newsFormData);
      } else {
        await createNews(newsFormData);
      }
      setShowAddForm(false);
      setEditingItem(null);
      resetNewsForm();
      loadNews();
      setError('');
    } catch (err) {
      setError('Failed to save news: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCommitmentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        await updateCommitment(editingItem._id, commitmentFormData);
      } else {
        await createCommitment(commitmentFormData);
      }
      setShowAddForm(false);
      setEditingItem(null);
      resetCommitmentForm();
      loadCommitments();
      setError('');
    } catch (err) {
      setError('Failed to save commitment: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMainImageSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        await updateMainImage(editingItem._id, mainImageFormData);
      } else {
        await createMainImage(mainImageFormData);
      }
      setShowAddForm(false);
      setEditingItem(null);
      resetMainImageForm();
      loadMainImages();
      setError('');
    } catch (err) {
      setError('Failed to save main image: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddForm(true);
    if (activeTab === 'news') {
      setNewsFormData({
        id: item.id,
        imageUrl: item.imageUrl,
        title: item.title,
        dateTime: item.dateTime ? new Date(item.dateTime).toISOString().slice(0, 16) : '',
        text: item.text,
        url: item.url || '',
        isActive: item.isActive
      });
    } else if (activeTab === 'commitments') {
      setCommitmentFormData({
        id: item.id,
        imageUrl: item.imageUrl,
        title: item.title,
        text: item.text,
        url: item.url || '',
        isActive: item.isActive
      });
    } else if (activeTab === 'commitment-main-image') {
      setMainImageFormData({
        id: item.id,
        imageUrl: item.imageUrl,
        isActive: item.isActive
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    setLoading(true);
    try {
      if (activeTab === 'news') {
        await deleteNews(id);
        loadNews();
      } else if (activeTab === 'commitments') {
        await deleteCommitment(id);
        loadCommitments();
      } else if (activeTab === 'commitment-main-image') {
        await deleteMainImage(id);
        loadMainImages();
      }
      setError('');
    } catch (err) {
      setError('Failed to delete item: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    setLoading(true);
    try {
      if (activeTab === 'news') {
        await toggleNewsActive(id);
        loadNews();
      } else if (activeTab === 'commitments') {
        await toggleCommitmentActive(id);
        loadCommitments();
      } else if (activeTab === 'commitment-main-image') {
        await toggleMainImageActive(id);
        loadMainImages();
      }
      setError('');
    } catch (err) {
      setError('Failed to toggle active status: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetNewsForm = () => {
    setNewsFormData({
      id: '',
      imageUrl: '',
      title: '',
      dateTime: '',
      text: '',
      url: '',
      isActive: true
    });
  };

  const resetCommitmentForm = () => {
    setCommitmentFormData({
      id: '',
      imageUrl: '',
      title: '',
      text: '',
      url: '',
      isActive: true
    });
  };

  const resetMainImageForm = () => {
    setMainImageFormData({
      id: '',
      imageUrl: '',
      isActive: true
    });
  };

  const renderNewsContent = () => {
    return (
      <div className="tab-content">
        <div className="content-header">
          <h3>News Management</h3>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setShowAddForm(true);
              setEditingItem(null);
              resetNewsForm();
            }}
          >
            Add News
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showAddForm && (
          <div className="form-modal">
            <div className="form-container">
              <h4>{editingItem ? 'Edit News' : 'Add News'}</h4>
              <form onSubmit={handleNewsSubmit}>
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="text"
                    value={newsFormData.id}
                    onChange={(e) => setNewsFormData({ ...newsFormData, id: e.target.value })}
                    required
                    disabled={editingItem}
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={newsFormData.imageUrl}
                    onChange={(e) => setNewsFormData({ ...newsFormData, imageUrl: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={newsFormData.title}
                    onChange={(e) => setNewsFormData({ ...newsFormData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date & Time</label>
                  <input
                    type="datetime-local"
                    value={newsFormData.dateTime}
                    onChange={(e) => setNewsFormData({ ...newsFormData, dateTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    value={newsFormData.text}
                    onChange={(e) => setNewsFormData({ ...newsFormData, text: e.target.value })}
                    required
                    rows={5}
                  />
                </div>
                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    value={newsFormData.url}
                    onChange={(e) => setNewsFormData({ ...newsFormData, url: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newsFormData.isActive}
                      onChange={(e) => setNewsFormData({ ...newsFormData, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingItem(null);
                      resetNewsForm();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="items-list">
          {loading && <div className="loading">Loading...</div>}
          {newsList.map((news) => (
            <div key={news._id} className="item-card">
              <div className="item-header">
                <h4>{news.title}</h4>
                <div className="item-actions">
                  <button className="btn btn-small btn-secondary" onClick={() => handleEdit(news)}>
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-warning" 
                    onClick={() => handleToggleActive(news._id)}
                  >
                    {news.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => handleDelete(news._id)}>
                    Delete
                  </button>
                </div>
              </div>
              <p><strong>ID:</strong> {news.id}</p>
              <p><strong>Date:</strong> {new Date(news.dateTime).toLocaleString()}</p>
              <p><strong>Status:</strong> {news.isActive ? 'Active' : 'Inactive'}</p>
              <div className="item-content">
                <p>{news.text.length > 200 ? news.text.substring(0, 200) + '...' : news.text}</p>
              </div>
              {news.imageUrl && (
                <img src={news.imageUrl} alt={news.title} className="item-image" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCommitmentContent = () => {
    return (
      <div className="tab-content">
        <div className="content-header">
          <h3>Commitment Data</h3>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setShowAddForm(true);
              setEditingItem(null);
              resetCommitmentForm();
            }}
          >
            Add Commitment
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showAddForm && (
          <div className="form-modal">
            <div className="form-container">
              <h4>{editingItem ? 'Edit Commitment' : 'Add Commitment'}</h4>
              <form onSubmit={handleCommitmentSubmit}>
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="text"
                    value={commitmentFormData.id}
                    onChange={(e) => setCommitmentFormData({ ...commitmentFormData, id: e.target.value })}
                    required
                    disabled={editingItem}
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={commitmentFormData.imageUrl}
                    onChange={(e) => setCommitmentFormData({ ...commitmentFormData, imageUrl: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={commitmentFormData.title}
                    onChange={(e) => setCommitmentFormData({ ...commitmentFormData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    value={commitmentFormData.text}
                    onChange={(e) => setCommitmentFormData({ ...commitmentFormData, text: e.target.value })}
                    required
                    rows={5}
                  />
                </div>
                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    value={commitmentFormData.url}
                    onChange={(e) => setCommitmentFormData({ ...commitmentFormData, url: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={commitmentFormData.isActive}
                      onChange={(e) => setCommitmentFormData({ ...commitmentFormData, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingItem(null);
                      resetCommitmentForm();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="items-list">
          {loading && <div className="loading">Loading...</div>}
          {commitmentsList.map((commitment) => (
            <div key={commitment._id} className="item-card">
              <div className="item-header">
                <h4>{commitment.title}</h4>
                <div className="item-actions">
                  <button className="btn btn-small btn-secondary" onClick={() => handleEdit(commitment)}>
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-warning" 
                    onClick={() => handleToggleActive(commitment._id)}
                  >
                    {commitment.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => handleDelete(commitment._id)}>
                    Delete
                  </button>
                </div>
              </div>
              <p><strong>ID:</strong> {commitment.id}</p>
              <p><strong>Status:</strong> {commitment.isActive ? 'Active' : 'Inactive'}</p>
              <div className="item-content">
                <p>{commitment.text.length > 200 ? commitment.text.substring(0, 200) + '...' : commitment.text}</p>
              </div>
              <div className="item-images">
                {commitment.imageUrl && (
                  <img src={commitment.imageUrl} alt={commitment.title} className="item-image" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCommitmentMainImageContent = () => {
    return (
      <div className="tab-content">
        <div className="content-header">
          <h3>Commitment Main Images</h3>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setShowAddForm(true);
              setEditingItem(null);
              resetMainImageForm();
            }}
          >
            Add Main Image
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showAddForm && (
          <div className="form-modal">
            <div className="form-container">
              <h4>{editingItem ? 'Edit Main Image' : 'Add Main Image'}</h4>
              <form onSubmit={handleMainImageSubmit}>
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="text"
                    value={mainImageFormData.id}
                    onChange={(e) => setMainImageFormData({ ...mainImageFormData, id: e.target.value })}
                    required
                    disabled={editingItem}
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={mainImageFormData.imageUrl}
                    onChange={(e) => setMainImageFormData({ ...mainImageFormData, imageUrl: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={mainImageFormData.isActive}
                      onChange={(e) => setMainImageFormData({ ...mainImageFormData, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingItem(null);
                      resetMainImageForm();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="items-list">
          {loading && <div className="loading">Loading...</div>}
          {mainImagesList.map((image) => (
            <div key={image._id} className="item-card">
              <div className="item-header">
                <h4>ID: {image.id}</h4>
                <div className="item-actions">
                  <button className="btn btn-small btn-secondary" onClick={() => handleEdit(image)}>
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-warning" 
                    onClick={() => handleToggleActive(image._id)}
                  >
                    {image.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => handleDelete(image._id)}>
                    Delete
                  </button>
                </div>
              </div>
              <p><strong>Status:</strong> {image.isActive ? 'Active' : 'Inactive'}</p>
              <div className="item-images">
                {image.imageUrl && (
                  <img src={image.imageUrl} alt={`Main Image ${image.id}`} className="item-image" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'news':
        return renderNewsContent();
      case 'commitments':
        return renderCommitmentContent();
      case 'commitment-main-image':
        return renderCommitmentMainImageContent();
      default:
        return null;
    }
  };

  return (
    <div className="news-commitments">
      <div className="tab-header">
        <h2>News and Commitments</h2>
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            <i className="fas fa-newspaper"></i>
            News
          </button>
          <button
            className={`tab-button ${activeTab === 'commitments' ? 'active' : ''}`}
            onClick={() => setActiveTab('commitments')}
          >
            <i className="fas fa-handshake"></i>
            Commitment Data
          </button>
          <button
            className={`tab-button ${activeTab === 'commitment-main-image' ? 'active' : ''}`}
            onClick={() => setActiveTab('commitment-main-image')}
          >
            <i className="fas fa-images"></i>
            Commitment Main Image
          </button>
        </div>
      </div>
      <div className="tab-body">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default NewsAndCommitments;